import React, { useState, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Avatar } from './Avatar';
import { ImageCropModal } from './ImageCropModal';
import { Button } from './Button';
import { Alert } from './Alert';
import { AddressInput } from './AddressInput';
import { userAPI } from '../api/userAPI';
import { toInputDate } from '../utils/format';
import { validatePhone, validateDate } from '../utils/validation';

export function ProfileEditModal({ user, onClose }) {
    const { fetchProfile } = useAuth();
    const photoInputRef = useRef(null);

    const [form, setForm] = useState({
        first_name: user.first_name || '',
        middle_initial: user.middle_initial || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        address: user.address || '',
        latitude: user.latitude || null,
        longitude: user.longitude || null,
        birthdate: toInputDate(user.birthdate),
        wedding_anniversary: toInputDate(user.wedding_anniversary),
    });
    const [photoUrl, setPhotoUrl] = useState(user.photo_url || null);
    const [cropFile, setCropFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [formErrors, setFormErrors] = useState({});

    const handlePhotoSelect = (e) => {
        const file = e.target.files[0];
        if (file) setCropFile(file);
        e.target.value = '';
    };

    const handleCroppedUpload = async (croppedFile) => {
        setCropFile(null);
        try {
            const res = await userAPI.uploadProfilePhoto(croppedFile);
            const newPhotoUrl = res.data?.data?.photo_url;
            if (newPhotoUrl) {
                setPhotoUrl(newPhotoUrl);
                setSuccess('Photo updated');
                fetchProfile();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to upload photo');
        }
    };

    const validateForm = () => {
        const errors = {};
        const phoneErr = validatePhone(form.phone);
        if (phoneErr) errors.phone = phoneErr;
        const bdErr = validateDate(form.birthdate, 'Birthdate');
        if (bdErr) errors.birthdate = bdErr;
        const waErr = validateDate(form.wedding_anniversary, 'Wedding anniversary');
        if (waErr) errors.wedding_anniversary = waErr;
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) return;
        setSaving(true);
        setError(null);
        try {
            await userAPI.updateProfile(form);
            await fetchProfile();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const today = new Date().toISOString().split('T')[0];

    return (
        <div className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center p-4" onClick={onClose}>
            <div
                className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
                    <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Edit Profile</h2>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="overflow-y-auto flex-1 p-4">
                    {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
                    {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

                    {/* Photo section */}
                    <div className="flex items-center gap-4 mb-5">
                        <Avatar name={user.name} photoUrl={photoUrl} size="xl" />
                        <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhotoSelect} />
                        <Button size="sm" variant="secondary" onClick={() => photoInputRef.current?.click()}>Change Photo</Button>
                    </div>
                    {cropFile && <ImageCropModal file={cropFile} onCrop={handleCroppedUpload} onCancel={() => setCropFile(null)} />}

                    {/* Form */}
                    <form id="profile-form" className="grid sm:grid-cols-2 gap-4" onSubmit={handleSubmit}>
                        <div className="sm:col-span-2 flex gap-3">
                            <div className="flex flex-col gap-1.5 flex-1">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">First Name *</label>
                                <input className="input-field" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                            </div>
                            <div className="flex flex-col gap-1.5 w-16">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">M.I.</label>
                                <input className="input-field text-center" maxLength={1} value={form.middle_initial} onChange={(e) => setForm({ ...form, middle_initial: e.target.value })} />
                            </div>
                            <div className="flex flex-col gap-1.5 flex-1">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Last Name *</label>
                                <input className="input-field" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                            </div>
                        </div>
                        <div className="flex flex-col gap-1.5 sm:col-span-2">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Phone</label>
                            <input className="input-field" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                            {formErrors.phone && <span className="text-red-500 text-xs mt-0.5">{formErrors.phone}</span>}
                        </div>
                        <div className="flex flex-col gap-1.5 sm:col-span-2">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Address</label>
                            <AddressInput
                                value={form.address}
                                onChange={({ address, latitude, longitude }) => setForm({ ...form, address, latitude, longitude })}
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Birthdate</label>
                            <input className="input-field" type="date" max={today} value={form.birthdate} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} />
                            {formErrors.birthdate && <span className="text-red-500 text-xs mt-0.5">{formErrors.birthdate}</span>}
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Wedding Anniversary</label>
                            <input className="input-field" type="date" max={today} value={form.wedding_anniversary} onChange={(e) => setForm({ ...form, wedding_anniversary: e.target.value })} />
                            {formErrors.wedding_anniversary && <span className="text-red-500 text-xs mt-0.5">{formErrors.wedding_anniversary}</span>}
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div className="flex gap-3 justify-end p-4 border-t border-slate-200 dark:border-slate-700">
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button type="submit" form="profile-form" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
                </div>
            </div>
        </div>
    );
}
